import dayjs from "dayjs";
import Accordion from "react-bootstrap/Accordion";
import { Permission, PermissionAction, User as UserData, UserGroup } from "../../../../models/User";
import { DateFormats } from "../../../../utilities/dates";
import Settings from "./settings/Settings";
import { UsersProps } from "./Users";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../store";
import { capitaliseFirst } from "../../../../utilities/helpers";
import {
    useGetProjectsQuery,
    useGetUserGroupsQuery,
    useGetUserPermissionsQuery,
} from "../../../../store/api/kinesense";
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import OverlaySpinner from "../../../../components/OverlaySpinner";
import ConditionalSpinner from "../../../../components/ConditionalSpinner";
import { set } from "immer/dist/internal";

export interface UserOwnProps {
    userData: UserData;
    isCurrentUser: boolean;
    isExpanded: boolean;
}
export type UserProps = UserOwnProps & UsersProps;

interface PermissionsTableProps {
    permissions: Permission[];
    actionColumns: PermissionAction[];
    isEditable?: boolean;
    showTarget?: boolean;
    targetTitle?: string;
    onPermissionsChanged?: (permissions: Permission[]) => void;
}

function PermissionsTable(props: PermissionsTableProps) {
    const { data: projects } = useGetProjectsQuery();
    const { data: userGroups, isLoading: isLoadingGroups } = useGetUserGroupsQuery();
    const [permissions, setPermissions] = useState<Map<string, Permission>>(
        props.permissions.reduce((map, obj) => {
            map.set(obj.permissionId, obj);
            return map;
        }, new Map()),
    );

    const userGroupsMap = useMemo(() => {
        return (
            userGroups?.reduce(
                (acc, group) => {
                    acc[group.groupId] = group;
                    return acc;
                },
                {} as Record<string, UserGroup>,
            ) ?? {}
        );
    }, [userGroups]);

    const actionInfoText = {
        peek: "Allows a user to know a resource exists and its name, but not further details",
    };
    const createResourceString = (permission: Permission) => {
        if (permission.condition === undefined) {
            return "All";
        }

        switch (permission.subject) {
            case "projects":
                return projects
                    ? projects.find((p) => p.projectId == permission.condition.projectId)?.name
                    : "Unknown Project";
            default:
                return `Unknown`;
        }
    };
    const getUserGroupName = (groupId: string | undefined) => {
        const unknownGroupName = "Unknown";

        if (groupId === undefined) {
            return unknownGroupName;
        }

        return userGroupsMap[groupId]?.name ?? unknownGroupName;
    };

    useEffect(() => {
        setPermissions(
            props.permissions.reduce((map, obj) => {
                map.set(obj.permissionId, obj);
                return map;
            }, new Map()),
        );
    }, [props.permissions]);

    useEffect(() => {
        props.onPermissionsChanged?.(Array.from(permissions.values()));
    }, [permissions]);

    const handlePermissionChanged = (permissionId: string, action: PermissionAction, checked: boolean) => {
        setPermissions((prev) => {
            const perms = [...(prev.get(permissionId)?.actions ?? [])];
            const removedPermissions = [];
            const newPermissions = [];

            if (checked) {
                if (!perms.includes(action)) {
                    newPermissions.push(action);

                    if (action == "read" && !perms.includes("peek")) {
                        newPermissions.push("peek");
                    }

                    if (action == "update" || (action == "delete" && !perms.includes("read"))) {
                        if (!perms.includes("read")) {
                            newPermissions.push("read");
                        }
                        if (!perms.includes("peek")) {
                            newPermissions.push("peek");
                        }
                    }

                    perms.push(...newPermissions);
                }
            } else {
                const index = perms.indexOf(action);
                if (index > -1) {
                    perms.splice(index, 1);
                    removedPermissions.push(action);
                }
            }

            return new Map(prev).set(permissionId, { ...prev.get(permissionId), actions: perms });
        });
    };

    return (
        <Table className="permissions-table">
            <thead>
                <tr>
                    <th style={{ width: "10%" }}>Permission</th>
                    {props.showTarget && <th style={{ width: "15%" }}>{props.targetTitle ?? "Target"}</th>}
                    <th
                        style={{
                            width: "10%",
                        }}
                    >
                        Resource
                    </th>
                    {props.actionColumns.map((action) => (
                        <th key={action} style={{ width: "10%", textAlign: "center" }} title={actionInfoText[action]}>
                            {capitaliseFirst(action)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from(permissions.values())
                    .sort((a, b) => a.subject.localeCompare(b.subject))
                    .map((permission) => (
                        <tr key={permission.permissionId}>
                            <td>{capitaliseFirst(permission.subject)}</td>
                            {props.showTarget && (
                                <td
                                    style={{
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {getUserGroupName(permission.target?.targetId)}
                                </td>
                            )}
                            <td
                                style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {createResourceString(permission)}{" "}
                            </td>

                            {props.actionColumns.map((action) => {
                                const forceDisabled =
                                    (action == "peek" || action == "read") &&
                                    (permission.actions.includes("update") || permission.actions.includes("delete"));

                                return (
                                    <td key={action} style={{ textAlign: "center" }}>
                                        <Form.Check
                                            type="checkbox"
                                            disabled={!(props.isEditable ?? false) || forceDisabled}
                                            readOnly
                                            checked={permission.actions.includes(action)}
                                            onChange={(e) =>
                                                handlePermissionChanged(
                                                    permission.permissionId,
                                                    action,
                                                    e.target.checked,
                                                )
                                            }
                                        />
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
            </tbody>
        </Table>
    );
}

function UserDetails(props: { user: UserData }) {
    const filterPermissions = (type: "user" | "group") => {
        return (permissions ?? []).filter((p) => p.target.type === type);
    };

    const { data: permissions, isLoading: isLoadingPermissions } = useGetUserPermissionsQuery({
        username: props.user.username,
    });
    const highestRole = props.user.isAdmin() ? "Admin" : props.user.isManager() ? "Manager" : "User";

    const [userPermissions, setUserPermissions] = useState<Permission[]>(() => filterPermissions("user"));
    const [groupPermissions, setGroupPermissions] = useState<Permission[]>(() => filterPermissions("group"));
    const [updatedUserPermissions, setUpdatedUserPermissions] = useState<Permission[]>([]);
    const [updatedGroupPermissions, setUpdatedGroupPermissions] = useState<Permission[]>([]);

    const { data: userGroups } = useGetUserGroupsQuery();
    const usersGroups = useMemo(
        () => (userGroups ?? []).filter((group) => props.user.groupIds.includes(group.groupId)),
        [userGroups, props.user],
    );

    if (!props.user) {
        return <div></div>;
    }

    useEffect(() => {
        setUserPermissions(filterPermissions("user"));
        setGroupPermissions(filterPermissions("group"));
    }, [permissions]);

    const handleUserPermissionsChanged = (permissions: Permission[]) => {
        setUpdatedUserPermissions(permissions);
    };

    const handleGroupPermissionsChanged = (permissions: Permission[]) => {
        setUpdatedGroupPermissions(permissions);
    };

    const handleResetUserPermissions = () => {
        setUserPermissions([]);
        setUserPermissions(filterPermissions("user"));
        setUpdatedUserPermissions([]);
    };

    const handleResetGroupPermissions = () => {
        setGroupPermissions([]);
        setGroupPermissions(filterPermissions("group"));
        setUpdatedGroupPermissions([]);
    };

    const handleSaveUserPermissions = () => {
        const updatedPermissionsMap = new Map(updatedUserPermissions.map((p) => [p.permissionId, p]));

        const newPermissionActions = new Map<string, PermissionAction[]>();
        const removedPermissionActions = new Map<string, PermissionAction[]>();

        for (const permission of userPermissions) {
            const updatedPermission = updatedPermissionsMap.get(permission.permissionId);
            if (updatedPermission) {
                const addedActions = updatedPermission.actions.filter((action) => !permission.actions.includes(action));
                if (addedActions.length > 0) {
                    newPermissionActions.set(permission.permissionId, addedActions);
                }

                const removedActions = permission.actions.filter(
                    (action) => !updatedPermission.actions.includes(action),
                );
                if (removedActions.length > 0) {
                    removedPermissionActions.set(permission.permissionId, removedActions);
                }
            } else {
                removedPermissionActions.set(permission.permissionId, permission.actions);
            }
        }
    };

    return (
        <div className="user-details">
            <Table>
                <tbody>
                    <tr>
                        <td>Username</td>
                        <td>{props.user.username}</td>
                    </tr>
                    <tr>
                        <td>Email</td>
                        <td>{props.user.email}</td>
                    </tr>
                    <tr>
                        <td>Role</td>
                        <td>{highestRole}</td>
                    </tr>
                    <tr>
                        <td>Joined at</td>
                        <td>{dayjs(props.user.joinedAt).format(DateFormats.dayMonthYearWithTime)}</td>
                    </tr>
                    <tr>
                        <td style={{ borderBottomWidth: "0px" }}>Groups</td>
                        <td style={{ borderBottomWidth: "0px" }}>
                            {userGroups ? usersGroups.map((group) => group.name).join(", ") : "Loading..."}
                        </td>
                    </tr>
                </tbody>
            </Table>

            <Accordion style={{ width: "100%" }}>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>User Permissions</Accordion.Header>
                    <Accordion.Body>
                        <div className="text-muted" style={{ textAlign: "center", marginBottom: "1rem" }}>
                            These are the permissions that are specific to the user <em>{props.user.username}</em>.
                        </div>
                        <ConditionalSpinner isLoading={isLoadingPermissions || userPermissions === undefined}>
                            {userPermissions.length === 0 ? (
                                <Alert variant="info" className="text-center">
                                    No user specific permissions set.
                                </Alert>
                            ) : (
                                <>
                                    <PermissionsTable
                                        permissions={userPermissions}
                                        isEditable={false}
                                        onPermissionsChanged={handleUserPermissionsChanged}
                                        actionColumns={["peek", "read", "manage", "create", "update", "delete"]}
                                    />

                                    {/* <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
                                        <Button onClick={handleResetUserPermissions} variant="secondary">
                                            Reset Changes
                                        </Button>
                                        <Button
                                            disabled={updatedPermissions.length === 0}
                                            onClick={handleSaveUserPermissions}
                                        >
                                            Save Changes
                                        </Button>
                                    </div> */}
                                </>
                            )}
                        </ConditionalSpinner>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <Accordion>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Group Permissions</Accordion.Header>
                    <Accordion.Body>
                        <div className="text-muted" style={{ textAlign: "center", marginBottom: "1rem" }}>
                            These are the permissions that the user <em>{props.user.username}</em> has due to membership
                            of user groups.
                        </div>
                        <ConditionalSpinner isLoading={isLoadingPermissions || groupPermissions === undefined}>
                            {groupPermissions.length === 0 ? (
                                <Alert variant="info" className="text-center">
                                    No group permissions set
                                </Alert>
                            ) : (
                                <PermissionsTable
                                    permissions={groupPermissions}
                                    isEditable={false}
                                    onPermissionsChanged={handleGroupPermissionsChanged}
                                    actionColumns={["peek", "read", "manage", "create", "update", "delete"]}
                                    showTarget={true}
                                    targetTitle={"User Group"}
                                />
                            )}
                        </ConditionalSpinner>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </div>
    );
}

export default function User(props: UserProps) {
    const currentUser = useSelector((state: ApplicationState) => state.user);
    const hasPrivilegedPermissions = currentUser.permissions.isAdmin || currentUser.permissions.isManager;
    const highestRole = props.userData.isAdmin() ? "Admin" : props.userData.isManager() ? "Manager" : "User";

    return (
        <Accordion.Item eventKey={props.userData.username}>
            <Accordion.Header>
                <span>
                    {props.userData.username}
                    {props.isCurrentUser ? " (You)" : ""}
                </span>
                <span>{props.userData.email}</span>

                <span>{highestRole}</span>
            </Accordion.Header>
            <Accordion.Body className="ps-5">
                <section>{props.isExpanded && <UserDetails user={props.userData} />}</section>

                {hasPrivilegedPermissions && !props.isCurrentUser && (
                    <>
                        <hr />
                        <Settings
                            userData={props.userData}
                            organisation={props.organisation}
                            isForCurrentUsersOrganisation={props.isForCurrentUsersOrganisation}
                        />
                    </>
                )}
            </Accordion.Body>
        </Accordion.Item>
    );
}
